import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import { Button } from '../../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { isValid } from 'date-fns';
import { ChartPieInteractive } from '../../../../components/ui/chart-pie-interactive';
import { ChartRadialShape } from '../../../../components/ui/chart-radial-shape';
import { ChartRadialStacked } from '../../../../components/ui/chart-radial-stacked';
import { ChartPieLabel } from '../../../../components/ui/chart-pie-label';
import { Card, CardContent } from '../../../../components/ui/card';
import {
  Download,
  Filter,
  RefreshCw,
  Search,
  BarChart3,
  PieChart,
  LineChart,
  Calendar,
  TrendingUp,
  TrendingDown,
  Eye,
  FileText,
  Users,
  CreditCard,
  ShoppingBag,
  Package,
  ChefHat,
  Building,
  Table as TableIcon,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { format } from 'date-fns';
import { DatePicker } from '../../../../components/ui/date-picker';

export function DashboardLayout({
  data,
  orderData,
  onDateChange,
  onBranchChange,
  onRefresh,
  loading,
  children,
  activeTab,
  onTabChange,
  onExport,
  onViewDetails,
  exporting = false
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date()
  });

  const safeFormat = (date, pattern) => {
    if (!date || !isValid(date)) return '--';
    return format(date, pattern);
  };

  const [filters, setFilters] = useState({
    orderType: 'all',
    paymentMode: 'all',
    orderMode: 'all',
    status: 'all'
  });

  const calculateGrowth = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  const formatCurrency = (amount) => {
    return `Rs ${new Intl.NumberFormat('en-PK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)}`;
  };

  const handleDateChange = (dates) => {
    const normalized = {
      startDate: dates?.startDate || dates?.from || null,
      endDate: dates?.endDate || dates?.to || null,
    };

    setDateRange(normalized);
    onDateChange?.(normalized);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({
      orderType: 'all',
      paymentMode: 'all',
      orderMode: 'all',
      status: 'all'
    });
  };

  const handleExport = (type) => {
    if (onExport) onExport(type);
  };

  const handleViewDetails = (reportType) => {
    if (onViewDetails) onViewDetails(reportType);
  };

  // Calculate metrics from data
  const getMetrics = () => {
    if (!data) return {};

    const paidOrders = orderData?.filter(order => order.OrderStatus === 'Paid') || [];
    const cashSales = paidOrders.filter(order => order.PaymentMode === 'CASH').reduce((sum, order) => sum + (order.NetAmount || 0), 0);
    const cardSales = paidOrders.filter(order => order.PaymentMode === 'CARD').reduce((sum, order) => sum + (order.NetAmount || 0), 0);

    // Mock previous day data (in real app, this would come from API)
    const previousData = {
      netSale: 50000,
      gross: 52000,
      tax: 7800,
      orders: 18
    };

    return {
      totalOrders: data.Table?.[0]?.TotalOrders_Landed || 0,
      grossSale: data.Table?.[0]?.Gross || 0,
      netSale: data.Table?.[0]?.NetSale || 0,
      totalTax: data.Table?.[0]?.Tax || 0,
      deliveryCharges: data.Table?.[0]?.DeliveryCharges || 0,
      totalDiscount: data.Table?.[0]?.Discount || 0,
      cashSale: data.Table8?.[0]?.OrderCount,
      cardSale: data.Table8?.[1]?.OrderCount || 0,
      growthNet: calculateGrowth(data.Table?.[0]?.NetSale || 0, previousData.netSale),
      growthGross: calculateGrowth(data.Table?.[0]?.Gross || 0, previousData.gross),
      growthOrders: calculateGrowth(data.Table?.[0]?.TotalOrders_Landed || 0, previousData.orders)
    };
  };

  const metrics = getMetrics();

  if (loading || !data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 w-48 bg-muted rounded animate-pulse" />
            <div className="h-4 w-64 bg-muted rounded animate-pulse mt-2" />
          </div>
          <div className="flex gap-2">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-10 w-24 bg-muted rounded animate-pulse" />
            ))}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-muted rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sales Dashboard</h1>
          {dateRange.startDate && dateRange.endDate && (
            <p className="text-muted-foreground">
              Comprehensive analytics for{' '}
              {format(dateRange.startDate, 'MMM dd, yyyy')} -{' '}
              {format(dateRange.endDate, 'MMM dd, yyyy')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onRefresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport('all')}
            disabled={exporting}>
            <Download className={`h-4 w-4 mr-2`} />
            {exporting ? 'Exporting...' : 'Export All'}
          </Button>
          <Button onClick={() => handleViewDetails('overview')}>
            <Eye className="h-4 w-4 mr-2" />
            View Details
          </Button>
        </div>
      </div>

      {/* Advanced Filters */}
      <Card className="border-2">
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Search and Date Range */}
            <div className="flex flex-col md:flex-row gap-4">

              <div className="flex gap-2">
                <DatePicker
                  date={dateRange}
                  onSelect={handleDateChange}
                />

                <Select value={filters.orderType} onValueChange={(value) => setFilters(prev => ({ ...prev, orderType: value }))}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Order Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="dinein">Dine In</SelectItem>
                    <SelectItem value="takeaway">Takeaway</SelectItem>
                    <SelectItem value="delivery">Delivery</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.paymentMode} onValueChange={(value) => setFilters(prev => ({ ...prev, paymentMode: value }))}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Payment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Payments</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.orderMode} onValueChange={(value) => setFilters(prev => ({ ...prev, orderMode: value }))}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Order Mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Modes</SelectItem>
                    <SelectItem value="pos">POS</SelectItem>
                    <SelectItem value="web">Web</SelectItem>
                    <SelectItem value="call">Call</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="outline" onClick={handleClearFilters} className="gap-2">
                  <Filter className="h-4 w-4" />
                  Clear All Filters
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics Cards with Growth Indicators */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">

        <ChartRadialShape
          netSale={metrics.netSale}
          growthNet={metrics.growthNet}
          totalOrders={metrics.totalOrders}
          targetSales={20000}
        />

        <ChartRadialStacked
          grossSale={metrics.grossSale}
          growthGross={metrics.growthGross}
          totalTax={metrics.totalTax}
        />

        <ChartPieLabel
          totalOrders={metrics.totalOrders}
          deliveryCharges={metrics.deliveryCharges}
          totalDiscount={metrics.totalDiscount}
        />

        <ChartPieInteractive
          cashAmount={metrics.cashSale}
          cardAmount={metrics.cardSale}
          title="Payment Split"
          description="Cash vs Card payments breakdown"
        />

      </div>

      {/* Quick Stats Row */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Order Value</p>
                <p className="text-2xl font-bold">
                  {metrics.totalOrders > 0 ? formatCurrency(metrics.netSale / metrics.totalOrders) : formatCurrency(0)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Customer Growth</p>
                <p className="text-2xl font-bold">
                  {metrics.growthOrders > 0 ? '+' : ''}{metrics.growthOrders.toFixed(1)}%
                </p>
              </div>
              <div className={`h-12 w-12 rounded-full ${metrics.growthOrders >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'} flex items-center justify-center`}>
                {metrics.growthOrders >= 0 ? (
                  <TrendingUp className="h-6 w-6 text-green-500" />
                ) : (
                  <TrendingDown className="h-6 w-6 text-red-500" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tax Rate</p>
                <p className="text-2xl font-bold">
                  {metrics.grossSale > 0 ? ((metrics.totalTax / metrics.grossSale) * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Discount Rate</p>
                <p className="text-2xl font-bold">
                  {metrics.grossSale > 0 ? ((metrics.totalDiscount / metrics.grossSale) * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-yellow-500/10 flex items-center justify-center">
                <TrendingDown className="h-6 w-6 text-yellow-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={onTabChange} className="space-y-4">
        <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 h-auto">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="sales" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Sales
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-2">
            <ShoppingBag className="h-4 w-4" />
            Orders
          </TabsTrigger>
          <TabsTrigger value="products" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Products
          </TabsTrigger>
          <TabsTrigger value="customers" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Customers
          </TabsTrigger>
          <TabsTrigger value="staff" className="flex items-center gap-2">
            <ChefHat className="h-4 w-4" />
            Staff
          </TabsTrigger>
          <TabsTrigger value="tables" className="flex items-center gap-2">
            <TableIcon className="h-4 w-4" />
            Tables
          </TabsTrigger>
          <TabsTrigger value="comparison" className="flex items-center gap-2">
            <LineChart className="h-4 w-4" />
            Comparison
          </TabsTrigger>
        </TabsList>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport(activeTab)}
            className="gap-2"
            disabled={exporting}
          >
            <Download className={`h-4 w-4`} />
            Export {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
          </Button>
          <Button
            size="sm"
            onClick={() => handleViewDetails(activeTab)}
            className="gap-2"
          >
            <Eye className="h-4 w-4" />
            View Details
          </Button>
        </div>

        {/* Render children (the tab contents) */}
        {children}
      </Tabs>
    </div>
  );
}